let provisionalWindow: Window | null = null;
let cleanupTimer: number | undefined;
let installed = false;
let clickHandler: ((event: MouseEvent) => void) | undefined;

const CLEANUP_MS = 12_000;
const NAVIGATION_DELAY_MS = 48;

export function paintProvisionalLoadingState(win: Window): void {
  try {
    const d = win.document;
    d.open();
    d.write(`<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<title>Loading…</title>
<style>
  html,body{height:100%;margin:0;font-family:system-ui,-apple-system,BlinkMacSystemFont,sans-serif;background:#f2f2f7;color:#1c1c1e}
  main{display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100%;gap:16px;padding:24px;text-align:center}
  .spinner{width:32px;height:32px;border:3px solid #d1d1d6;border-top-color:#0071e3;border-radius:50%;animation:sp .85s linear infinite}
  @keyframes sp{to{transform:rotate(360deg)}}
  p{margin:0;font-size:17px;line-height:1.35;max-width:28rem}
  .sub{font-size:15px;color:#636366}
</style></head><body><main>
  <div class="spinner" role="status" aria-label="Loading"></div>
  <p>Loading your experience…</p>
  <p class="sub">If this takes too long, you can close this tab and try again.</p>
</main></body></html>`);
    d.close();
  } catch {
    // Child may have navigated or become inaccessible.
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function paintProvisionalErrorState(
  win: Window,
  heading: string,
  detail: string,
): void {
  try {
    const { href } = win.location;
    if (!href.startsWith('about:')) return;
    const d = win.document;
    d.open();
    d.write(`<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<title>${escapeHtml(heading)}</title>
<style>
  html,body{height:100%;margin:0;font-family:system-ui,-apple-system,sans-serif;background:#f2f2f7;color:#1c1c1e}
  main{max-width:36rem;margin:0 auto;padding:32px 24px}
  h1{font-size:22px;margin:0 0 12px}
  p{font-size:15px;line-height:1.4;color:#3a3a3c;margin:0 0 8px;word-break:break-word}
  .detail{font-size:14px;color:#636366}
</style></head><body><main>
  <h1>${escapeHtml(heading)}</h1>
  <p class="detail">${escapeHtml(detail)}</p>
</main></body></html>`);
    d.close();
  } catch {
    // Same as paintProvisionalLoadingState.
  }
}

export function beginProvisionalNavigation(win: Window, url: string): void {
  let hostLabel = url;
  try {
    hostLabel = new URL(url).host;
  } catch {
    // Non-URL string: show as-is in title.
  }

  try {
    const d = win.document;
    d.open();
    d.write(`<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<title>${escapeHtml(hostLabel)}</title>
<style>
  html,body{height:100%;margin:0;font-family:system-ui,-apple-system,BlinkMacSystemFont,sans-serif;background:#f2f2f7;color:#1c1c1e}
  main{max-width:40rem;margin:0 auto;padding:28px 20px 40px}
  .spinner{width:28px;height:28px;border:3px solid #d1d1d6;border-top-color:#0071e3;border-radius:50%;animation:sp .85s linear infinite;margin-bottom:18px}
  @keyframes sp{to{transform:rotate(360deg)}}
  h1{font-size:20px;font-weight:600;margin:0 0 10px}
  .url{font-size:15px;line-height:1.45;word-break:break-all;background:#fff;border:1px solid #d1d1d6;border-radius:10px;padding:14px 16px;margin:0 0 16px}
  .hint{font-size:14px;line-height:1.45;color:#636366;margin:0}
</style></head><body><main>
  <div class="spinner" role="status" aria-label="Loading"></div>
  <h1>Opening site</h1>
  <p class="url" role="status">${escapeHtml(url)}</p>
  <p class="hint">Safari will show this address in the tab once the connection starts. If the site cannot be reached, Safari will display its own error for that address.</p>
</main></body></html>`);
    d.close();
  } catch {
    try {
      win.location.assign(url);
    } catch {
      paintProvisionalErrorState(
        win,
        'Could not open this page',
        'The address could not be opened from this browser tab.',
      );
    }
    return;
  }

  window.setTimeout(() => {
    if (win.closed) return;
    try {
      win.location.assign(url);
    } catch {
      paintProvisionalErrorState(
        win,
        'Could not open this page',
        'Safari blocked navigation, or the address was invalid.',
      );
    }
  }, NAVIGATION_DELAY_MS);
}

function clearCleanupTimer(): void {
  if (cleanupTimer !== undefined) {
    window.clearTimeout(cleanupTimer);
    cleanupTimer = undefined;
  }
}

function scheduleCleanup(): void {
  clearCleanupTimer();
  const captured = provisionalWindow;
  cleanupTimer = window.setTimeout(() => {
    cleanupTimer = undefined;
    if (captured && !captured.closed) {
      try {
        const { href } = captured.location;
        if (
          href === 'about:blank' ||
          href === '' ||
          href.startsWith('about:')
        ) {
          captured.close();
        }
      } catch {
        // Navigated away from about: — do not close.
      }
    }
    if (provisionalWindow === captured) {
      provisionalWindow = null;
    }
  }, CLEANUP_MS);
}

function shouldOpenProvisionalForTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  if (el.closest('[data-provisional-external-tab]')) return true;
  const btn = el.closest('button');
  if (!btn || !btn.closest('main')) return false;
  const label =
    (btn.textContent || '') + (btn.getAttribute('aria-label') || '');
  return /\bget started\b/i.test(label);
}

function isStrictPopupWebKit(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/iP(ad|hone|od)/i.test(ua)) return true;
  if (!/safari/i.test(ua)) return false;
  if (/chrome|chromium|crios|fxios|edgios|opr\//i.test(ua)) return false;
  return true;
}

export function installSafariProvisionalExternalTabListener(): void {
  if (typeof document === 'undefined' || installed) return;
  if (!isStrictPopupWebKit()) return;
  installed = true;

  clickHandler = (event: MouseEvent): void => {
    if (event.defaultPrevented) return;
    if (!shouldOpenProvisionalForTarget(event.target)) return;

    if (provisionalWindow && !provisionalWindow.closed) {
      try {
        provisionalWindow.close();
      } catch {
        // Ignore close failures.
      }
    }

    provisionalWindow = window.open(
      'about:blank',
      '_blank',
      'noopener,noreferrer',
    );
    if (provisionalWindow) {
      paintProvisionalLoadingState(provisionalWindow);
      scheduleCleanup();
    }
  };

  document.addEventListener('click', clickHandler, true);
}

export function uninstallSafariProvisionalExternalTabListener(): void {
  if (!clickHandler) return;
  document.removeEventListener('click', clickHandler, true);
  clickHandler = undefined;
  installed = false;
  clearCleanupTimer();
  provisionalWindow = null;
}

export function consumeProvisionalExternalTab(): Window | null {
  const w = provisionalWindow;
  provisionalWindow = null;
  clearCleanupTimer();
  return w;
}
